import { AlertCircle, CheckCircle, ExternalLink, Globe, Rocket, X } from 'lucide-react';

interface Entity {
    storeUrl?: string;
}

interface PublishResult {
    success: boolean;
    error?: string;
}

interface Props {
    entity: Entity | null | undefined;
    // Confirmation modal
    showConfirm: boolean;
    onConfirmClose: () => void;
    onConfirmPublish: () => void;
    // Progress / result modal
    isPublishing: boolean;
    publishResult: PublishResult | null;
    onResultClose: () => void;
    onRetry: () => void;
}

export default function PublishModal({
    entity,
    showConfirm,
    onConfirmClose,
    onConfirmPublish,
    isPublishing,
    publishResult,
    onResultClose,
    onRetry,
}: Props) {
    const showProgressModal = isPublishing || publishResult !== null;

    if (!showConfirm && !showProgressModal) return null;

    return (
        <>
            {/* Publish confirmation modal */}
            {showConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center">
                                        <Rocket className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-900">Publish Your Website</h2>
                                </div>
                                <button onClick={onConfirmClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            <p className="text-gray-600 mb-6">
                                Are you ready to make your website live? This will publish your store for customers to visit and shop.
                            </p>

                            {entity?.storeUrl && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                                    <div className="flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-green-600" />
                                        <span className="text-sm text-green-700">
                                            Your website will be available at:{' '}
                                            <strong>{entity.storeUrl}.mart-24.com</strong>
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    onClick={onConfirmClose}
                                    className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirmPublish}
                                    className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-blue-600 border border-transparent rounded-lg hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 flex items-center justify-center gap-2"
                                >
                                    <Rocket className="w-4 h-4" />
                                    Publish Now
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Publishing progress / result modal */}
            {showProgressModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform animate-in slide-in-from-bottom-4 duration-300">
                        <div className="p-6">
                            {isPublishing ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center relative">
                                        <Rocket className="w-8 h-8 text-white relative z-10" />
                                        <div className="absolute inset-0 animate-spin rounded-full border-4 border-white/30 border-t-white" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Publishing Your Website</h2>
                                    <p className="text-gray-600">Please wait while we make your website live…</p>
                                </div>
                            ) : publishResult?.success ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                                        <CheckCircle className="w-10 h-10 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Successfully Published! 🎉</h2>
                                    <p className="text-gray-600 mb-6">Your website is now live and ready for customers to visit.</p>
                                    {entity?.storeUrl && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                            <div className="flex items-center justify-between">
                                                <span className="text-green-700 text-sm font-medium">
                                                    {entity.storeUrl}.mart-24.com
                                                </span>
                                                <button
                                                    onClick={() => window.open(`https://${entity.storeUrl}.mart-24.com`, '_blank')}
                                                    className="flex items-center gap-1 px-3 py-1.5 text-xs text-green-700 bg-green-100 hover:bg-green-200 rounded-lg transition-colors font-medium"
                                                >
                                                    <ExternalLink className="w-3 h-3" />
                                                    Visit Website
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        onClick={onResultClose}
                                        className="w-full px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 border border-transparent rounded-lg hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center">
                                        <AlertCircle className="w-10 h-10 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Publishing Failed</h2>
                                    <p className="text-gray-600 mb-6">
                                        {publishResult?.error || 'Something went wrong while publishing your website.'}
                                    </p>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={onResultClose}
                                            className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                        >
                                            Close
                                        </button>
                                        <button
                                            onClick={onRetry}
                                            className="flex-1 px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
